import { getServerProps } from './serverProps';
import { isLoggedInUser } from './authUtils';
import _includes from 'lodash/includes';
import _get from 'lodash/get';
import _keys from 'lodash/keys';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import getStore from '../store/configureStore';
import { getParameterByName } from '../../utility/queryStringHelper';
import { setTitleCase } from './commonUtils';
import { showSpinner } from './spinnerUtils';
import templateData from '../../utility/getTemplateData';

const serverProps = getServerProps();
const { userType } = serverProps;

export const WIZARD_TOTAL_STEPS = isHostedFlow() ? 4 : 3;

export function getNextStep(currentStep, flowType) {
  const totalSteps = getWizardTotalSteps(flowType);
  if (currentStep < totalSteps) {
    return currentStep + 1;
  }
  return 1;
}

export function getPreviousStep(currentStep) {
  if (currentStep > 1) {
    return currentStep - 1;
  }
  return 1;
}

export function getNextPageUrl(currentPage, currentPath) {
  const nextPage = currentPage === WIZARD_TOTAL_STEPS ? 0 : currentPage + 1;

  return `${currentPath}?page=${nextPage}`;
}

export function getPreviousPageUrl(currentPage, currentPath) {
  const previousPage = currentPage === 1 ? 1 : currentPage - 1;

  return `${currentPath}?page=${previousPage}`;
}

export function getPageUrl(pageNumber, currentPath) {
  const page =
    pageNumber >= 1 && pageNumber <= WIZARD_TOTAL_STEPS ? pageNumber : 1;

  return `${currentPath}?page=${page}`;
}

export function isHostedFlow() {
  return (
    isLoggedInUser(serverProps) && _includes(['BUSINESS', 'PREMIER'], userType)
  );
}

export function isUnhostedFlow() {
  return isLoggedInUser(serverProps) && userType === 'PERSONAL';
}

/**
 * Fetch select options for language field based on the country selection
 */
export const getOptionsForLanguageField = (
  countryAndLanguageDetails,
  selectedCountry
) => {
  const countriesByCode = countryAndLanguageDetails?.countriesByCode;

  selectedCountry = selectedCountry || 'US';

  const allLanguages = countriesByCode?.[selectedCountry]?.languages;
  const languageCodes = _keys(allLanguages);

  return _map(languageCodes, (languageCode) => {
    return {
      value: languageCode,
      displayValue: setTitleCase(allLanguages?.[languageCode]?.name),
    };
  });
};

export const getOptionsForCountriesField = (countryAndLanguageDetails) => {
  const countriesByCode = countryAndLanguageDetails.countriesByCode;
  const countryCodes = _keys(countriesByCode);

  return _map(countryCodes, (countryCode) => {
    return {
      value: countryCode,
      displayValue: _get(countriesByCode, `${countryCode}.name`),
    };
  });
};

// Button Factory V3 Specific Utils
export function isBF3Enabled() {
  const reduxStore = getStore();
  return _get(reduxStore.getState(), 'application.enableBf3');
}

export function getFlowType() {
  let flowType;
  const reduxStore = getStore();
  const state = reduxStore.getState();
  const storeFlowType =
    _get(state, 'buttonManagerV3.formattedButton.flowType') ||
    _get(state, 'buttonFactoryV3.formattedButton.flowType');

  if (templateData.isFederated) {
    flowType = getParameterByName('type');
    if (_isEmpty(flowType) || !/^[CS]$/.test(flowType)) {
      return storeFlowType || 'C';
    }
  } else {
    flowType = storeFlowType;
    if (_isEmpty(flowType)) {
      return getParameterByName('type') || 'C';
    }
  }
  return flowType;
}

export const isDonateButton = (type) => {
  const flowType = type || getFlowType();

  return flowType.toUpperCase() === 'C' || flowType.toUpperCase() !== 'S';
};

export const isDonateLink = (type) => {
  const flowType = type || getFlowType();

  return flowType === 'S';
};

export function getWizardTotalSteps(type) {
  const flowType = type || getFlowType();

  if (isDonateLink(flowType) && isUnhostedFlow()) {
    return 2;
  } else if (isDonateLink(flowType) || isUnhostedFlow()) {
    return 3;
  }

  return 4;
}

export function getStepUrl(currentLocation, currentStep, targetStep, flowType) {
  // For BF V3
  let { pathname, search } = currentLocation;
  const step =
    targetStep >= 1 && targetStep <= getWizardTotalSteps(flowType)
      ? targetStep
      : 1;

  if (search.includes('page')) {
    search = search.replace(`page=${currentStep}`, `page=${step}`);
  } else if (!_isEmpty(search)) {
    search = `${search}&page=${step}`;
  } else {
    search = `${search}?page=${step}`;
  }

  return `${pathname}${search}`;
}

export function isMobileOrTablet() {
  return window.innerWidth <= 1024;
}

export function isTablet() {
  return window.innerWidth >= 577 && window.innerWidth <= 1024;
}

export function getUserType() {
  return userType;
}

export function getEncryptedAccountNum() {
  return serverProps?.accountIdentifier?.encrypted_account_number;
}

export function getSavedButtonsCount() {
  return serverProps?.listButtons?.length;
}

export const isGuestFormPreview = (currentStep) =>
  (isDonateButton() && currentStep === 4) ||
  (isDonateLink() && currentStep === 3);

export const handleLoginHelper = (history) => {
  const renderURL = '/donate/buttons/login?returnUri=/buttons/manage';
  history.push(renderURL);
  showSpinner();
  window.location.reload();
};

export const handleSignupHelper = (history) => {
  const federatedURL = templateData.isFederated ? '/mep' : '';
  const renderURL = `/bizsignup/entry?product=donations&returnUrl=https://${window.location.hostname}${federatedURL}/donate/buttons/manage`;
  history.push(renderURL);
  showSpinner();
  window.location.reload();
};

export const isCsamHit = (error) => {
  // returns true if user uploads a restricted image
  return error?.details?.[0]?.issue?.includes('CSAM hit detected');
};

export const scrollToWizardsTop = (isMobile) => {
  if (typeof document === 'undefined') return;
  if (isMobile) {
    window.scrollTo(0, 0);
  } else {
    const wizardContainer = document.getElementsByClassName(
      'wizard-step-container'
    );
    if (wizardContainer.length > 0) {
      wizardContainer[0].scrollTop = 0;
    }
  }
};

export const calculateAspectRatioToFit = (
  imageWidth,
  imageHeight,
  containerWidth,
  containerHeight
) => {
  // Calculate the original aspect ratio of the image
  const originalAspectRatio = imageWidth / imageHeight;

  // Calculate the aspect ratio of the container
  const containerAspectRatio = containerWidth / containerHeight;

  // Calculate the aspect ratio based on the maximum crop size
  let aspectRatio;
  if (originalAspectRatio >= containerAspectRatio) {
    aspectRatio = Math.min(containerAspectRatio, containerWidth / imageHeight);
  } else {
    aspectRatio = Math.min(containerAspectRatio, containerHeight / imageWidth);
  }
  return aspectRatio;
};
